import * as React from 'react'
import { CssBaseline, ThemeProvider } from '@material-ui/core'
import { useMemo } from 'react'
import makeTheme from 'theme'
import { useGetOrganization } from 'hooks/useGetOrganization'
import { themeColors } from 'constants/theme/colors'
import { OrganizationTheme } from 'types'
import CircularSpinner from '../../panel/components/CircularSpinner'
import { colorNameToHex } from '../../utils/colorUtils'

const AppThemeProvider: React.FC = ({ children }) => {
  const { organization, subDomain, loading } = useGetOrganization()

  const settingsKey = subDomain?.includes('test') ? 'draftSettings' : 'publishedSettings'

  const palette = useMemo(
    () =>
      organization?.whiteLabel
        ? organization?.[settingsKey]?.Typography?.ColorScheme === 'custom'
          ? {
              main: organization?.[settingsKey]?.Typography?.ThemeCustomization?.ColorScheme?.WhiteMode?.CustomScheme
                ?.PrimaryColor
                ? colorNameToHex(
                    organization[settingsKey].Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme
                      .PrimaryColor
                  )
                : themeColors.primary.main,
              500: organization?.[settingsKey]?.Typography?.ThemeCustomization?.ColorScheme?.WhiteMode?.CustomScheme
                ?.SecondaryColor?.Color
                ? colorNameToHex(
                    organization[settingsKey].Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme
                      .SecondaryColor.Color
                  )
                : themeColors.primary[500],
              600: organization?.[settingsKey]?.Typography?.ThemeCustomization?.ColorScheme?.WhiteMode?.CustomScheme
                ?.PrimaryColor
                ? colorNameToHex(
                    organization[settingsKey].Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme
                      .PrimaryColor
                  )
                : themeColors.primary[600],
              700: organization?.[settingsKey]?.Typography?.ThemeCustomization?.ColorScheme?.WhiteMode?.CustomScheme
                ?.TertiaryColor?.Color
                ? colorNameToHex(
                    organization[settingsKey].Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme
                      .TertiaryColor.Color
                  )
                : themeColors.primary[700],
              contrastText:
                organization?.[settingsKey]?.Typography?.ButtonTextColor || themeColors.primary.contrastText,
            }
          : themeColors[(organization?.[settingsKey]?.Typography?.ColorScheme as OrganizationTheme) || 'primary']
        : themeColors.primary,
    [organization, settingsKey]
  )

  const fontFamily = useMemo(
    () => (organization?.whiteLabel ? organization?.[settingsKey]?.Typography?.BodyFontFamily : 'Proxima Nova'),
    [organization, settingsKey]
  )

  const headerFontFamily = useMemo(
    () => (organization?.whiteLabel ? organization?.[settingsKey]?.Typography?.HeaderFontFamily : 'Proxima Nova'),
    [organization, settingsKey]
  )

  const radius = useMemo(
    () => (organization?.whiteLabel ? organization?.[settingsKey]?.Typography?.ButtonCornerRadius : '0'),
    [organization, settingsKey]
  )

  const themeColor = useMemo(
    () =>
      organization?.whiteLabel
        ? organization?.[settingsKey]?.Typography?.ThemeCustomization?.SelectedMode === 'DarkMode'
          ? '#fff'
          : '#000'
        : '#fff',
    [organization, settingsKey]
  )

  const themeBackgroundColor = useMemo(
    () =>
      organization?.whiteLabel
        ? organization?.[settingsKey]?.Typography?.ThemeCustomization?.SelectedMode === 'DarkMode'
          ? '#000'
          : '#fff'
        : '#000',
    [organization, settingsKey]
  )

  const theme = useMemo(
    () =>
      makeTheme({
        primary: palette,
        fontFamily,
        headerFontFamily,
        radius,
        themeColor,
        themeBackgroundColor,
      }),
    [palette, fontFamily, headerFontFamily, radius, themeColor, themeBackgroundColor]
  )

  if (loading) {
    return <CircularSpinner className='is-center' />
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}

export default AppThemeProvider
